import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBookOpen, FaEdit, FaEye, FaCheckCircle, FaTimes, FaStar, FaTrash } from 'react-icons/fa';
import { auth, db, storage } from '../firebase';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import BackButton from '../components/BackButton';
import { backendConfig } from '../config/backendConfig';
import { API_ENDPOINTS } from '../config/backendConfig';
import { saveStoryActivity } from '../utils/activityService';
import { toJSDate } from '../utils/date';
import KidBackground from '../ui/backgrounds/KidBackground';
import splashImage from '../assets/splash.png';
import { Home, LayoutDashboard, LogOut } from 'lucide-react';
import { goToArtistCorner, goToHomeMarketing } from '../utils/navigationPaths';

// const backendBase = backendConfig.baseUrl;

// Custom Modal Component
const CustomModal = ({ isOpen, onClose, title, message, type = 'info', onConfirm }) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <FaStar className="text-4xl text-amber-400" />;
      case 'info':
        return <FaBookOpen className="text-4xl text-amber-400" />;
      default:
        return <FaBookOpen className="text-4xl text-amber-400" />;
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border-2 border-amber-200 max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-amber-600 hover:text-amber-800 transition-colors"
        >
          <FaTimes className="text-xl" />
        </button>
        
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            {getIcon()}
          </div>
          <h3 className="text-2xl font-bold text-amber-800 mb-3">{title}</h3>
          <p className="text-amber-700 text-lg leading-relaxed whitespace-pre-line">{message}</p>
          
          <div className="mt-6 flex gap-3 justify-center">
            {onConfirm && (
              <button
                onClick={handleConfirm}
                className="bg-gradient-to-r from-red-500 to-red-400 hover:from-red-600 hover:to-red-500 text-white font-bold py-3 px-6 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl"
              >
                Yes, Delete
              </button>
            )}
            <button
              onClick={onClose}
              className={`bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-600 hover:to-amber-500 text-white font-bold py-3 px-8 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl ${onConfirm ? '' : 'w-full'}`}
            >
              {onConfirm ? 'Cancel' : 'Wonderful!'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function MyArtStories() {
  // Inline placeholder to avoid 404s when public asset not present
  const PLACEHOLDER_DATA_URL = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA2NCA5NiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iOTYiIGZpbGw9IiNGM0VCQTIiIHJ4PSIxMiIvPjx0ZXh0IHg9IjMyIiB5PSI0OCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1zaXplPSIxMiIgZmlsbD0iI0Q2OTI0MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null });
  const [currentUser, setCurrentUser] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  // Resolve image URL (handles BFL CDN, relative paths, and localhost URLs)
  // For localhost URLs, converts them to backend proxy URLs
  const resolveImageUrl = (url) => {
    if (!url || typeof url !== 'string') return PLACEHOLDER_DATA_URL;
    const trimmed = url.trim();
    if (!trimmed) return PLACEHOLDER_DATA_URL;
    
    // localhost:3001 → convert to backend proxy URL (backend will fetch from Firebase Storage)
    // Handle URLs with or without query parameters
    if (trimmed.includes('localhost:3001')) {
      // Extract path manually (more reliable than URL parsing)
      // Remove protocol and host, then remove query parameters
      const urlPath = trimmed
        .replace(/^https?:\/\/[^/]+/, '') // Remove http://localhost:3001
        .split('?')[0] // Remove query parameters like ?t=123456
        .trim();
      
      if (urlPath && urlPath.startsWith('/')) {
        // Use backend proxy-image endpoint which handles /uploads/ paths and fetches from Firebase Storage
        const base = API_ENDPOINTS?.backendBase || process.env.REACT_APP_BACKEND_URL || 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api';
        const resolved = `${base}/proxy-image?url=${encodeURIComponent(urlPath)}`;
        console.log(`🔄 [MyArtStories] Resolved localhost URL: ${trimmed.substring(0, 80)}... → ${resolved.substring(0, 100)}...`);
        return resolved;
      }
      
      console.warn(`⚠️ [MyArtStories] Could not extract path from localhost URL: ${trimmed}`);
    }
    
    // Proxy BFL CDN through backend to avoid CORS / auth issues
    if (trimmed.includes('bfl.ai') || trimmed.includes('delivery-eu4.bfl.ai')) {
      const base = API_ENDPOINTS?.backendBase || process.env.REACT_APP_BACKEND_URL || 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api';
      return `${base}/proxy-image?url=${encodeURIComponent(trimmed)}`;
    }
    // Prefix relative paths with backend base
    if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('data:')) {
      const base = API_ENDPOINTS?.backendBase || process.env.REACT_APP_BACKEND_URL || 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api';
      return `${base}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`;
    }
    return trimmed;
  };

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        // Redirect to login if not authenticated
        navigate('/login', { state: { from: '/my-art-stories' } });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!currentUser) return; // Don't fetch if no user is logged in
    
    setLoading(true);
    setError(null);
    
    try {
      const storiesCollectionRef = collection(db, 'stories');
      const q = query(storiesCollectionRef, where('userId', '==', currentUser.uid));
      
      // Use real-time listener instead of one-time fetch
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const fetchedStories = querySnapshot.docs.map(doc => {
          const data = doc.data();
          // LEGACY SUPPORT: some older stories saved pages under `pages` instead of `scenes`
          const legacyPages = Array.isArray(data.pages) && (!Array.isArray(data.scenes) || data.scenes.length === 0)
            ? (data.pages || []).map((p, idx) => ({
                sceneNumber: p.sceneNumber || idx + 1,
                title: p.title || p.pageTitle || `Scene ${idx + 1}`,
                description: p.description || p.text || '',
                imageUrl: p.imageUrl || p.image || p.refinedImageUrl || p.coverUrl || '',
                refinedImageUrl: p.refinedImageUrl || '',
                url: p.url || '',
                image: p.image || ''
              }))
            : null;
          const normalized = {
            id: doc.id,
            ...data,
            scenes: Array.isArray(data.scenes) && data.scenes.length > 0 ? data.scenes : (legacyPages || [])
          };
          return normalized;
        });
        
        setStories(fetchedStories);
        // Attempt to resolve any legacy Firebase Storage paths (gs://) to downloadable URLs
        try {
          resolveLegacyFirebaseImages(fetchedStories);
        } catch (_) {}
        setLoading(false);
      }, (error) => {
        console.error('❌ Error fetching stories:', error);
        setError(error.message);
        setLoading(false);
      }, { purpose: 'MyArtStories-stories', component: 'MyArtStories' });
      
      // Cleanup subscription on unmount
      return () => unsubscribe();
      
    } catch (err) {
      console.error('❌ Error setting up stories listener:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [currentUser]);

  // Cache for resolved Firebase download URLs to avoid repeated calls
  const firebaseUrlCache = React.useRef(new Map());

  // Convert gs:// paths to https download URLs and update stories state
  const resolveLegacyFirebaseImages = async (storiesList) => {
    const updated = await Promise.all((storiesList || []).map(async (story) => {
      let changed = false;
      const newStory = { ...story };

      // Helper to resolve one URL
      const resolveOne = async (url) => {
        if (!url || typeof url !== 'string') return url;
        const trimmed = url.trim();
        if (!trimmed.startsWith('gs://')) return url;
        // Use cache
        if (firebaseUrlCache.current.has(trimmed)) return firebaseUrlCache.current.get(trimmed);
        try {
          // Convert gs://bucket/path -> path after bucket
          const withoutScheme = trimmed.replace(/^gs:\/\//, '');
          const firstSlash = withoutScheme.indexOf('/');
          const objectPath = firstSlash >= 0 ? withoutScheme.slice(firstSlash + 1) : withoutScheme;
          const r = storageRef(storage, objectPath);
          const dl = await getDownloadURL(r);
          firebaseUrlCache.current.set(trimmed, dl);
          return dl;
        } catch (err) {
          console.warn('Failed to resolve Firebase Storage URL', trimmed, err.message);
          return url;
        }
      };

      // Cover URL
      if (newStory.coverUrl && newStory.coverUrl.startsWith('gs://')) {
        newStory.coverUrl = await resolveOne(newStory.coverUrl);
        changed = true;
      }

      // Scenes
      if (Array.isArray(newStory.scenes)) {
        newStory.scenes = await Promise.all(newStory.scenes.map(async (scene) => {
          const s = { ...scene };
          const keys = ['refinedImageUrl','imageUrl','url','image','coverUrl'];
          for (const k of keys) {
            if (s[k] && typeof s[k] === 'string' && s[k].startsWith('gs://')) {
              s[k] = await resolveOne(s[k]);
              changed = true;
            }
          }
          return s;
        }));
      }

      return changed ? newStory : story;
    }));

    // Only update state if anything changed
    if (updated.some((s, i) => s !== storiesList[i])) {
      setStories(updated);
    }
  };

  const showModal = (title, message, type = 'info') => {
    setModal({ isOpen: true, title, message, type });
  };

  const closeModal = () => {
    setModal({ isOpen: false, title: '', message: '', type: 'info' });
  };

  const handlePublish = async (storyId) => {
    try {
      const storyRef = doc(db, 'stories', storyId);
      await updateDoc(storyRef, { status: 'published' });
      
      // Find the story data to save activity
      const story = stories.find(s => s.id === storyId);
      if (story) {
        // Save story activity to dashboard when published
        try {
          await saveStoryActivity({
            originalImageUrl: story.originalArtwork || story.coverUrl,
            storyTitle: story.storyTitle || story.title,
            storyDescription: story.about || story.brief || 'Story created from artwork',
            characters: story.characters || [],
            scenes: story.scenes || [],
            storyId: storyId,
            status: 'published'
          }, true); // Published
          console.log('✅ Story activity saved to dashboard (published)');
        } catch (activityError) {
          console.warn('⚠️ Failed to save story activity:', activityError);
        }
      }
      
      // Show congratulatory modal
      showModal(
        "🎉 Congratulations, Fellow Artist!",
        "Your book is now published and ready for the world to see! 📚✨\n\nReady to hold your masterpiece in your hands? Print your book and watch it arrive at your doorstep! 🚚",
        'success'
      );
    } catch (err) {
      showModal(
        "Oops! Something went wrong",
        "Don't worry, little artist! Let's try publishing your book again. Sometimes magic takes a moment to work! ✨",
        'info'
      );
    }
  };

  const handlePrintBook = (story) => {
    // Navigate to checkout with the story data for printing
    navigate('/checkout', { 
      state: { 
        printMode: true,
        selectedBooks: [{
          id: story.id,
          title: story.storyTitle || story.title,
          image: (story.scenes && story.scenes[0]?.refinedImageUrl) || (story.scenes && story.scenes[0]?.imageUrl) || story.coverUrl || '/book-cover-placeholder.png',
          price: 500, // Standard print cost
          type: 'print',
          author: story.author || 'Unknown',
          scenes: story.scenes?.length || 0,
          description: `Print your story "${story.storyTitle || story.title}" with Artmosphere`
        }]
      } 
    });
  };

  const handleDeleteStory = async (storyId, storyTitle) => {
    // Show confirmation modal first
    setModal({
      isOpen: true,
      title: "🗑️ Delete Your Story?",
      message: `Are you sure you want to delete "${storyTitle}"? This action cannot be undone, but don't worry - you can always create a new masterpiece! ✨`,
      type: 'info',
      onConfirm: async () => {
        try {
          const storyRef = doc(db, 'stories', storyId);
          await deleteDoc(storyRef);
          
          // Remove from local state
          setStories(stories => stories.filter(story => 
            story.id !== storyId
          ));
          
          // Show success message
          setModal({
            isOpen: true,
            title: "✨ Story Deleted Successfully!",
            message: "Your story has been removed. But remember, every artist creates many masterpieces! Ready to start a new adventure? 🎨",
            type: 'success',
            onConfirm: null
          });
        } catch (err) {
          setModal({
            isOpen: true,
            title: "Oops! Something went wrong",
            message: "Don't worry, little artist! Let's try deleting your story again. Sometimes magic takes a moment to work! ✨",
            type: 'info',
            onConfirm: null
          });
        }
      }
    });
  };

  // Filtered stories based on search and status
  const filteredStories = stories.filter(story => {
    const storyTitle = story.storyTitle || story.title || '';
    const matchesSearch = storyTitle.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || (statusFilter === "published" ? story.status === "published" : story.status !== "published");
    return matchesSearch && matchesStatus;
  });

  // Show loading state while checking authentication
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-amber-700 text-xl">Loading your creative space...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col relative overflow-hidden bg-[#FFF8E1] px-0 md:px-[2.5cm]">
      {/* Amber color splashes background - matching home page */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
      </div>
      
      {/* Splash images merged into background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-10 right-5 opacity-20"
          style={{ 
            width: '400px',
            height: '200px',
            objectFit: 'cover',
            transform: 'rotate(15deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(2px)'
          }}
        />
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
          style={{ 
            width: '300px',
            height: '150px',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%) rotate(10deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(3px)'
          }}
        />
      </div>
      <KidBackground variant="blobs" intensity={1} />

      {/* Window container — full width, flex-1 so only inner content scrolls */}
      <div className="relative z-10 w-full max-w-full font-sans flex-1 min-h-0 flex flex-col py-0 md:py-6 lg:py-8">
        <div className="bg-white/95 rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 p-3 md:p-6 flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Compact header — Art History Learning style */}
        <header className="flex-shrink-0 sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
          <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px]">
            <p className="text-sm md:text-base font-semibold text-gray-800">
              Publishing House
            </p>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={(e) => goToArtistCorner(navigate, e)}
                className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
                title="Artist Corner"
              >
                <LayoutDashboard className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                <span className="hidden md:inline ml-1">Artist Corner</span>
              </button>
              <button
                type="button"
                onClick={(e) => goToHomeMarketing(navigate, e)}
                className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
                title="Home"
              >
                <Home className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                <span className="hidden md:inline ml-1">Home</span>
              </button>
            </div>
          </div>
        </header>

        {/* Fixed top: intro, CTA, search/filter — no scroll */}
        <div className="flex-shrink-0 border-b border-amber-100/80 pb-4">
          <p className="text-sm text-slate-600 mt-3 mb-2">
            Start your author journey with Artmosphere
          </p>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              type="button"
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold py-2 px-4 rounded-full shadow-md text-sm transition-all"
              onClick={() => navigate('/storyboarding-landing')}
            >
              Once Upon a Click
            </button>
          </div>

          {/* Search and Filter Row */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between w-full">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search stories by title..."
              className="w-full sm:w-1/2 px-3 md:px-4 py-2 md:py-3 rounded-xl border-2 border-amber-300 focus:border-amber-500 focus:outline-none text-sm md:text-lg shadow-md bg-white"
              style={{ fontFamily: 'inherit' }}
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 md:px-4 py-2 md:py-3 rounded-xl border-2 border-amber-300 focus:border-amber-500 focus:outline-none text-sm md:text-lg shadow-md bg-white"
              style={{ fontFamily: 'inherit' }}
            >
              <option value="all">All Stories</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          {loading && <div className="text-slate-700 text-center mt-2">Loading stories...</div>}
          {error && <div className="text-red-600 text-center mt-2">{error}</div>}
        </div>

        {/* Scrollable stories area only */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-8 pt-4 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredStories.map(story => (
              <div key={story.id} className="relative flex flex-col md:flex-row items-stretch w-full hover:shadow-2xl transition-shadow duration-300 p-4 overflow-hidden rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 shadow-lg">
                {/* Removed movie icon; replaced with Add Scene control at top-right */}
                {/* Book Cover */}
                <div className="relative w-56 h-80 flex-shrink-0 mr-6 flex items-center justify-center">
                  <img
                  src={resolveImageUrl((story.scenes && story.scenes[0]?.refinedImageUrl) || (story.scenes && story.scenes[0]?.imageUrl) || story.coverUrl || PLACEHOLDER_DATA_URL)}
                    alt={story.storyTitle || story.title}
                    className="w-56 h-80 object-cover rounded-xl border-2 border-amber-100 bg-amber-50 shadow-md"
                    onError={(e) => {
                      const failedUrl = e.target.src;
                      console.error(`Failed to load story cover image:`, failedUrl);
                      
                      // If it's a localhost URL, try resolving it through the proxy
                      if (failedUrl.includes('localhost:3001')) {
                        const resolved = resolveImageUrl(failedUrl);
                        if (resolved !== failedUrl && resolved !== PLACEHOLDER_DATA_URL) {
                          console.log(`🔄 Retrying cover with resolved URL: ${resolved}`);
                          e.target.src = resolved;
                          return; // Don't set placeholder yet, let it try the resolved URL
                        }
                      }
                      
                      // Fallback to placeholder
                      e.target.src = PLACEHOLDER_DATA_URL;
                    }}
                  />
                  {/* Status Badge */}
                  <div className={`absolute top-2 left-2 px-3 py-1 rounded-full text-xs font-bold shadow ${story.status === 'published' ? 'bg-green-200 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {story.status === 'published' ? <span className="flex items-center gap-1"><FaCheckCircle /> Published</span> : 'Draft'}
                  </div>
                  {/* Top-right Add Scene button for storyboarding stories */}
                  {story.storySetupData && (
                    <button
                      className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-orange-400 text-white font-semibold py-1 px-3 rounded-full shadow text-xs hover:from-amber-600 hover:to-orange-500"
                      title="Add a new scene"
                      onClick={() => navigate(`/storyboarding-studio?storyId=${story.id}&editScene=${(story.scenes?.length || 0) + 1}`)}
                    >
                      + Scene
                    </button>
                  )}
                  {/* Overlay Buttons */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1 z-10">
                    <button
                      className="bg-amber-400 hover:bg-amber-500 text-white font-semibold py-0.5 px-2 rounded-lg shadow flex items-center gap-1 text-xs"
                      onClick={() => {
                        // For storyboarding stories, always go to book view if they have scenes
                        if (story.storySetupData && story.scenes && story.scenes.length > 0) {
                          navigate(`/view-book/${story.id}`);
                        } else if (story.storySetupData) {
                          // Storyboarding story without scenes - go to storyboarding studio
                          navigate(`/storyboarding-studio?storyId=${story.id}`);
                        } else {
                          // Regular book story
                          navigate(`/view-book/${story.id}`);
                        }
                      }}
                    >
                      <FaEye /> View/Edit
                    </button>
                    {/* Removed secondary + Scene button next to View */}
                      <button
                        className="bg-red-400 hover:bg-red-500 text-white font-semibold py-0.5 px-2 rounded-lg shadow flex items-center gap-1 text-xs"
                        onClick={() => handleDeleteStory(story.id, story.storyTitle || story.title)}
                      >
                        <FaTrash /> Delete
                      </button>
                  </div>
                </div>
                {/* Book Info, Actions, and Print Button (right column) */}
                <div className="flex flex-col flex-1 min-w-0 justify-between py-2">
                  <div className="flex flex-row justify-between items-start">
                    <div>
                      <div className="text-xl font-bold text-amber-800 mb-1 truncate max-w-full">{story.storyTitle || story.title}</div>
                      <div className="text-sm text-amber-600 mb-1">By {story.author || 'Unknown'}</div>
                      <div className="text-xs text-gray-400 mb-2">{story.updatedAt ? toJSDate(story.updatedAt)?.toLocaleDateString() : story.dateUpdated ? new Date(story.dateUpdated).toLocaleDateString() : ''}</div>
                    </div>
                    {story.status !== 'published' && (
                      <button
                        className="bg-amber-400 hover:bg-amber-500 text-white font-semibold text-sm py-1 px-3 rounded-full shadow ml-4 mt-1"
                        onClick={() => handlePublish(story.id)}
                      >
                        Publish
                      </button>
                    )}
                  </div>
                  {/* Tagline/Brief/Quote */}
                  {(story.brief || (story.scenes && story.scenes[0]?.sceneDescription)) && (
                    <div className="italic text-amber-700 bg-amber-100 rounded-lg px-3 py-2 mb-2 text-sm max-w-full truncate shadow-sm">
                      "{story.brief || story.scenes[0]?.sceneDescription}"
                    </div>
                  )}
                  
                  {/* Story Information */}
                  <div className="text-sm text-amber-600 space-y-1">
                    {story.storySetupData && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Main Character:</span>
                        <span>{story.storySetupData.mainCharacter?.name || 'Not set'}</span>
                      </div>
                    )}
                    {story.scenes && story.scenes.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Scenes:</span>
                        <span>{story.scenes.length} scenes (Scene {story.scenes[0]?.sceneNumber || 1})</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Total Scenes:</span>
                      <span>{story.totalScenes || story.scenes?.length || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Created:</span>
                      <span>{story.createdAt ? toJSDate(story.createdAt)?.toLocaleDateString() : story.dateCreated ? new Date(story.dateCreated).toLocaleDateString() : ''}</span>
                    </div>
                  </div>
                  {/* Scene Thumbnails */}
                  {Array.isArray(story.scenes) && story.scenes.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {story.scenes.slice(0, 3).map((scene, idx) => {
                        // Debug logging for scene data
                        const imageUrl = scene.refinedImageUrl || scene.imageUrl || scene.url || scene.image || scene.coverUrl || PLACEHOLDER_DATA_URL;
                        
                        if (process.env.NODE_ENV === 'development') {
                          console.log(`Scene ${idx + 1} data:`, {
                            sceneNumber: idx + 1,
                            refinedImageUrl: scene.refinedImageUrl,
                            imageUrl: scene.imageUrl,
                            url: scene.url,
                            image: scene.image,
                            coverUrl: scene.coverUrl,
                            finalImageUrl: imageUrl,
                            allKeys: Object.keys(scene),
                            storyType: story.storySetupData ? 'Storyboarding' : 'What\'s Your Story'
                          });
                        }
                        
                        return (
                          <img
                            key={idx}
                            src={resolveImageUrl((scene.refinedImageUrl && scene.refinedImageUrl.trim()) || 
                                 (scene.imageUrl && scene.imageUrl.trim()) || 
                                 (scene.url && scene.url.trim()) || 
                                 (scene.image && scene.image.trim()) || 
                                 (scene.coverUrl && scene.coverUrl.trim()) || 
                                 PLACEHOLDER_DATA_URL)}
                            alt={scene.sceneDescription || scene.description || scene.title || `Scene ${idx + 1}`}
                            className="w-16 h-16 object-cover rounded-lg border border-amber-100 bg-amber-50 shadow"
                            title={scene.sceneDescription || scene.description || scene.title || `Scene ${idx + 1}`}
                            onError={(e) => {
                              const failedUrl = e.target.src;
                              console.error(`Failed to load image for Scene ${idx + 1}:`, failedUrl);
                              
                              // If it's a localhost URL, try resolving it through the proxy
                              if (failedUrl.includes('localhost:3001')) {
                                const resolved = resolveImageUrl(failedUrl);
                                if (resolved !== failedUrl && resolved !== PLACEHOLDER_DATA_URL) {
                                  console.log(`🔄 Retrying with resolved URL: ${resolved}`);
                                  e.target.src = resolved;
                                  return; // Don't set placeholder yet, let it try the resolved URL
                                }
                              }
                              
                              // Fallback to placeholder
                              e.target.src = PLACEHOLDER_DATA_URL;
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                  {/* Pages/Scenes Count */}
                  <div className="mt-2 text-xs text-amber-600 font-semibold">
                    {Array.isArray(story.scenes) ? `Scenes: ${story.scenes.length}` : 'No scenes yet'}
                  </div>
                  {/* Print Button at the bottom of right column, full width */}
                  <button
                    className="w-full bg-amber-200 bg-opacity-60 hover:bg-amber-300 hover:bg-opacity-80 text-amber-900 font-bold py-3 rounded-b-2xl shadow flex items-center justify-center gap-2 animate-bounce text-lg border-t-2 border-amber-200 mt-4 backdrop-blur"
                    onClick={() => handlePrintBook(story)}
                  >
                    Print Your Book with Artmosphere
                  </button>
                </div>
              </div>
            ))}
          </div>
          {(!loading && filteredStories.length === 0) && (
            <div className="text-white text-center mt-12 text-lg drop-shadow-md">No stories found. Try a different search or filter!</div>
          )}
        </div>
      </div>
      </div>

      {/* Custom Modal */}
      <CustomModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
      />
    </div>
  );
} 